/*
  Sihoo brand stories — shared behaviour for the story-sihoo-* snippets
  (story-sihoo-c300-pro-v2.liquid, story-sihoo-m57.liquid). Everything is
  progressive: the markup reads fine with no JS (native scroll-snap tracks,
  <details>, posters instead of videos, the first tab panel visible).
  The data-c3-* hooks were named for the first (C300) story; they are shared.

  1. lazy videos         — <video data-c3-video>: sources attach near the
                           viewport, play in view, pause out of view / on a
                           hidden tab / with reduced motion
  2. carousels           — [data-c3-carousel]: prev/next scroll one slide;
                           counter + progress bar
  3. segmented tabs      — [data-c3-seg]
  4. spec table toggle   — [data-c3-spec-toggle]
  5. dialogs             — [data-c3-open="id"] opens <dialog id="id"> and
                           plays its <video> if it has one; [data-c3-close]
                           and a backdrop click close it
  6. YouTube facade      — <button data-c3-yt="VIDEO_ID"> inside a
                           [data-c3-yt-box]: the click swaps the poster for
                           an autoplaying youtube-nocookie iframe
  7. YouTube dialog      — [data-c3-yt-open="VIDEO_ID"] fills the shared
                           <dialog data-c3-yt-dialog> with an iframe and
                           opens it; closing removes the iframe
*/
(function () {
  'use strict';
  var D = document, W = window;
  var root = D.querySelector('[data-c3]');
  if (!root) return;
  var reduce = W.matchMedia && W.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var slice = function (list) { return Array.prototype.slice.call(list || []); };

  /* 1. lazy videos */
  var videos = slice(root.querySelectorAll('video[data-c3-video]'));
  function loadVideo(v) {
    if (v.getAttribute('data-c3-loaded')) return;
    var any = false;
    slice(v.querySelectorAll('source[data-src]')).forEach(function (s) {
      s.src = s.getAttribute('data-src');
      any = true;
    });
    if (!any) return;
    v.setAttribute('data-c3-loaded', '1');
    v.load();
  }
  function playVideo(v) {
    if (reduce) return;
    loadVideo(v);
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay refused: poster stays */ });
  }
  function pauseVideo(v) { if (!v.paused) v.pause(); }
  var inView = [];
  if (videos.length) {
    if ('IntersectionObserver' in W) {
      var loadIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          loadVideo(e.target);
          loadIO.unobserve(e.target);
        });
      }, { rootMargin: '320px 0px' });
      var playIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { inView.push(e.target); playVideo(e.target); }
          else { inView = inView.filter(function (v) { return v !== e.target; }); pauseVideo(e.target); }
        });
      }, { threshold: 0.2 });
      videos.forEach(function (v) { loadIO.observe(v); playIO.observe(v); });
    } else {
      videos.forEach(loadVideo);
    }
    /* pause everything on a hidden tab; resume the clips still in view on return */
    D.addEventListener('visibilitychange', function () {
      if (D.hidden) videos.forEach(pauseVideo);
      else inView.forEach(playVideo);
    });
  }

  /* 2. carousels */
  slice(root.querySelectorAll('[data-c3-carousel]')).forEach(function (car) {
    var track = car.querySelector('[data-c3-track]');
    if (!track) return;
    var slides = slice(track.children);
    var prev = car.querySelector('[data-c3-prev]');
    var next = car.querySelector('[data-c3-next]');
    var cur = car.querySelector('[data-c3-cur]');
    var fill = car.querySelector('[data-c3-fill]');
    var idx = 0, timer;
    var base = function () { return slides[0].offsetLeft; };
    function update() {
      var x = track.scrollLeft, best = 0, bestD = Infinity;
      slides.forEach(function (s, i) {
        var d = Math.abs((s.offsetLeft - base()) - x);
        if (d < bestD) { bestD = d; best = i; }
      });
      idx = best;
      if (cur) cur.textContent = String(idx + 1);
      if (fill) fill.style.width = ((idx + 1) / slides.length * 100) + '%';
      if (prev) prev.disabled = idx === 0;
      if (next) next.disabled = idx === slides.length - 1;
    }
    function go(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      var left = slides[i].offsetLeft - base();
      if (track.scrollTo) track.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' });
      else track.scrollLeft = left;
    }
    if (prev) prev.addEventListener('click', function () { go(idx - 1); });
    if (next) next.addEventListener('click', function () { go(idx + 1); });
    track.addEventListener('scroll', function () {
      clearTimeout(timer);
      timer = setTimeout(update, 80);
    }, { passive: true });
    W.addEventListener('resize', function () { clearTimeout(timer); timer = setTimeout(update, 120); });
    update();
  });

  /* 3. segmented tabs */
  slice(root.querySelectorAll('[data-c3-seg]')).forEach(function (seg) {
    var btns = slice(seg.querySelectorAll('[data-c3-seg-btn]'));
    var panels = slice(seg.querySelectorAll('[data-c3-seg-panel]'));
    btns.forEach(function (b, i) {
      b.addEventListener('click', function () {
        btns.forEach(function (x, j) {
          x.classList.toggle('is-active', i === j);
          x.setAttribute('aria-selected', i === j ? 'true' : 'false');
        });
        panels.forEach(function (p, j) { p.classList.toggle('is-active', i === j); });
      });
    });
  });

  /* 4. spec table toggle */
  slice(root.querySelectorAll('[data-c3-spec-toggle]')).forEach(function (btn) {
    var box = btn.closest('[data-c3-spec]');
    var label = btn.querySelector('span');
    if (!box) return;
    btn.addEventListener('click', function () {
      var open = box.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (label) label.textContent = open ? 'Show Less' : 'View All Specifications';
    });
  });

  /* 5. dialogs */
  function openDialog(dlg) {
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
  }
  slice(root.querySelectorAll('[data-c3-open]')).forEach(function (btn) {
    btn.addEventListener('click', function () {
      var dlg = D.getElementById(btn.getAttribute('data-c3-open'));
      if (!dlg) return;
      openDialog(dlg);
      var v = dlg.querySelector('video');
      if (v) {
        loadVideo(v);
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    });
  });
  slice(root.querySelectorAll('dialog')).forEach(function (dlg) {
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
    slice(dlg.querySelectorAll('[data-c3-close]')).forEach(function (b) {
      b.addEventListener('click', function () { dlg.close(); });
    });
    dlg.addEventListener('close', function () {
      var v = dlg.querySelector('video');
      if (v) pauseVideo(v);
    });
  });

  /* 6. YouTube facade */
  function ytFrame(id) {
    var f = D.createElement('iframe');
    f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1';
    f.title = 'YouTube video';
    f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    f.setAttribute('allowfullscreen', '');
    f.setAttribute('frameborder', '0');
    return f;
  }
  slice(root.querySelectorAll('[data-c3-yt]')).forEach(function (btn) {
    btn.addEventListener('click', function () {
      var box = btn.closest('[data-c3-yt-box]') || btn.parentNode;
      box.classList.add('is-playing');
      box.appendChild(ytFrame(btn.getAttribute('data-c3-yt')));
      btn.remove();
    });
  });

  /* 7. YouTube dialog */
  var ytDlg = root.querySelector('dialog[data-c3-yt-dialog]');
  if (ytDlg) {
    var ytBox = ytDlg.querySelector('[data-c3-yt-frame]') || ytDlg;
    slice(root.querySelectorAll('[data-c3-yt-open]')).forEach(function (btn) {
      btn.addEventListener('click', function () {
        ytBox.innerHTML = '';
        ytBox.appendChild(ytFrame(btn.getAttribute('data-c3-yt-open')));
        openDialog(ytDlg);
      });
    });
    ytDlg.addEventListener('close', function () { ytBox.innerHTML = ''; });
  }
})();
